// contexts/voice/useVoiceConnection.ts
// Hook for managing WebSocket connection and audio streaming

import { useState, useCallback, useRef, useEffect } from 'react';
import { ConnectionState, ChatMode, VadMode, UIAction, UIContext, GeminiVoiceId } from './types';
import { VOICE_CONSTANTS } from './constants';
import { AudioStreamer } from './AudioStreamer';
import { getIdToken } from '../../firebase';

interface RecorderRefs {
  stream: MediaStream;
  audioContext: AudioContext;
  source: MediaStreamAudioSourceNode;
  processor: ScriptProcessorNode;
}

interface UseVoiceConnectionProps {
  themeId: string;
  language: string;
  selectedVoice: GeminiVoiceId;
  vadMode: VadMode;
  actAsUser?: { email: string } | null;
  onTranscript: (text: string) => void;
  onResponse: (text: string) => void;
  onUIAction: (action: UIAction) => void;
  onToolCall: (tool: string, args: unknown) => void;
  onTurnComplete: () => void;
  onTyping: (isTyping: boolean) => void;
  onError: (error: string) => void;
}

export function useVoiceConnection({
  themeId,
  language,
  selectedVoice,
  vadMode,
  actAsUser,
  onTranscript,
  onResponse,
  onUIAction,
  onToolCall,
  onTurnComplete,
  onTyping,
  onError,
}: UseVoiceConnectionProps) {
  // Connection state
  const [state, setState] = useState<ConnectionState>('disconnected');
  const [chatMode, setChatMode] = useState<ChatMode>('voice');
  const [error, setError] = useState<string | null>(null);

  // Audio controls
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const [pushToTalkMode, setPushToTalkModeState] = useState(false);
  const pushToTalkModeRef = useRef(false);
  const [audioDetectedWhileMuted, setAudioDetectedWhileMuted] = useState(false);
  const audioDetectedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for WebSocket and audio
  const wsRef = useRef<WebSocket | null>(null);
  const playerRef = useRef<AudioStreamer | null>(null);
  const recorderRef = useRef<RecorderRefs | null>(null);
  const latestContextRef = useRef<UIContext | null>(null);
  const modelStartedSpeakingAtRef = useRef<number>(0);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const newMuted = !prev;
      mutedRef.current = newMuted;
      return newMuted;
    });
  }, []);

  // Push-to-talk controls
  const setPushToTalkMode = useCallback((enabled: boolean) => {
    setPushToTalkModeState(enabled);
    pushToTalkModeRef.current = enabled;
    if (enabled) {
      setMuted(true);
      mutedRef.current = true;
    } else {
      setMuted(false);
      mutedRef.current = false;
    }
  }, []);

  const startTalking = useCallback(() => {
    if (pushToTalkModeRef.current) {
      setMuted(false);
      mutedRef.current = false;
    }
  }, []);

  const stopTalking = useCallback(() => {
    if (pushToTalkModeRef.current) {
      setMuted(true);
      mutedRef.current = true;
    }
  }, []);

  // Send context to backend
  const sendContext = useCallback((context: UIContext) => {
    latestContextRef.current = context;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'context', ...context }));
    }
  }, []);

  // Send text message
  const sendTextMessage = useCallback((text: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (!text.trim()) return;

    onTyping(true);
    wsRef.current.send(JSON.stringify({
      type: 'message',
      text: text.trim()
    }));
  }, [onTyping]);

  // Close text chat session
  const closeChat = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (chatMode !== 'text') return;

    wsRef.current.send(JSON.stringify({ type: 'close_chat' }));
  }, [chatMode]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'audio':
          if (modelStartedSpeakingAtRef.current === 0) {
            modelStartedSpeakingAtRef.current = Date.now();
          }
          playerRef.current?.play(data.audio);
          break;

        case 'typing':
          onTyping(true);
          break;

        case 'response':
          onTyping(false);
          if (modelStartedSpeakingAtRef.current === 0) {
            modelStartedSpeakingAtRef.current = Date.now();
          }
          onResponse(data.text);
          setState('listening');
          break;

        case 'transcript':
          playerRef.current?.stop();  // Stop audio, don't report (interrupt will follow)
          modelStartedSpeakingAtRef.current = 0;
          onTranscript(data.text);
          setState('speaking');
          break;

        case 'interrupted': {
          // Report exactly how much agent audio was played before stopping
          const playedBytes = playerRef.current?.getPlayedBytes() ?? 0;
          playerRef.current?.stop();
          if (playedBytes > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'playback_stopped', played_bytes: playedBytes }));
          }
          modelStartedSpeakingAtRef.current = 0;
          setState('speaking');
          break;
        }

        case 'done':
          playerRef.current?.complete();
          modelStartedSpeakingAtRef.current = 0;
          onTurnComplete();
          setState('connected');
          break;

        case 'ui_action':
          onUIAction(data);
          break;

        case 'debug':
          if (data.event === 'tool_call') {
            onToolCall(data.tool, data.args);
          }
          break;

        case 'stop_audio':
          playerRef.current?.stop();
          break;

        case 'error':
          setError(data.message);
          onError(data.message);
          break;
      }
    } catch {
      // Ignore non-JSON messages
    }
  }, [onTranscript, onResponse, onUIAction, onToolCall, onTurnComplete, onTyping, onError]);

  // Connect to voice/text server
  const connect = useCallback(async (mode: ChatMode = 'voice') => {
    if (state !== 'disconnected') return;

    setChatMode(mode);
    setState('connecting');
    setError(null);

    try {
      // Initialize audio player for voice mode
      if (mode === 'voice') {
        const audioCtx = new AudioContext({ sampleRate: VOICE_CONSTANTS.AUDIO_SAMPLE_RATE_OUTPUT });
        playerRef.current = new AudioStreamer(audioCtx);
        playerRef.current.onComplete = () => {
          // Notify server that frontend speakers stopped playing
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'playback_finished' }));
          }
        };
      }

      // Get auth token
      const token = await getIdToken(true);
      if (!token) throw new Error('Authentication failed');

      // Connect WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const endpoint = mode === 'voice' ? VOICE_CONSTANTS.WS_VOICE_ENDPOINT : VOICE_CONSTANTS.WS_TEXT_ENDPOINT;
      let wsUrl = `${protocol}//${window.location.host}/${endpoint}?token=${encodeURIComponent(token)}&agent=tools&theme=${encodeURIComponent(themeId)}&lang=${encodeURIComponent(language)}&voice=${encodeURIComponent(selectedVoice)}&vad=${encodeURIComponent(vadMode)}`;
      if (actAsUser?.email) {
        wsUrl += `&act_as=${encodeURIComponent(actAsUser.email)}`;
      }
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        setState('connected');

        // Send initial context if available
        if (latestContextRef.current) {
          ws.send(JSON.stringify({ type: 'context', ...latestContextRef.current }));
        }

        // Start audio recording for voice mode
        if (mode === 'voice') {
          const useRawAudio = false; // Browser echo cancellation always ON
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: !useRawAudio,
              noiseSuppression: !useRawAudio,
              autoGainControl: !useRawAudio,
            },
          });
          const audioContext = new AudioContext({ sampleRate: VOICE_CONSTANTS.AUDIO_SAMPLE_RATE_INPUT });
          const source = audioContext.createMediaStreamSource(stream);
          const processor = audioContext.createScriptProcessor(VOICE_CONSTANTS.AUDIO_BUFFER_SIZE, 1, 1);

          recorderRef.current = { stream, audioContext, source, processor };

          processor.onaudioprocess = (e) => {
            if (ws.readyState !== WebSocket.OPEN) return;

            const inputData = e.inputBuffer.getChannelData(0);

            // If muted, handle PTT hint and send silence
            if (mutedRef.current) {
              if (pushToTalkModeRef.current) {
                let sum = 0;
                for (let i = 0; i < inputData.length; i++) {
                  sum += inputData[i] * inputData[i];
                }
                const rms = Math.sqrt(sum / inputData.length);
                if (rms > 0.02) {
                  setAudioDetectedWhileMuted(true);
                  if (audioDetectedTimeoutRef.current) clearTimeout(audioDetectedTimeoutRef.current);
                  audioDetectedTimeoutRef.current = setTimeout(() => setAudioDetectedWhileMuted(false), VOICE_CONSTANTS.PTT_HINT_TIMEOUT_MS);
                }
              }
              const silentData = new ArrayBuffer(inputData.length * 2);
              ws.send(silentData);
              return;
            }

            // Convert to PCM and send
            const pcmData = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              pcmData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
            }
            ws.send(pcmData.buffer);
          };

          source.connect(processor);
          processor.connect(audioContext.destination);

          // If PTT mode, start muted
          if (pushToTalkModeRef.current) {
            setMuted(true);
            mutedRef.current = true;
          }
        }
      };

      ws.onmessage = handleMessage;

      ws.onclose = () => {
        setState('disconnected');
      };

      ws.onerror = () => {
        setError('Connection lost');
        disconnect();
      };

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setState('disconnected');
    }
  }, [state, themeId, language, selectedVoice, vadMode, actAsUser, handleMessage]);

  // Disconnect
  const disconnect = useCallback(() => {
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Stop and cleanup audio recorder
    if (recorderRef.current) {
      recorderRef.current.processor.disconnect();
      recorderRef.current.source.disconnect();
      recorderRef.current.audioContext.close();
      recorderRef.current.stream.getTracks().forEach(track => track.stop());
      recorderRef.current = null;
    }

    // Stop and cleanup audio player
    if (playerRef.current) {
      playerRef.current.stop();
      if (playerRef.current.context.state !== 'closed') {
        playerRef.current.context.close();
      }
      playerRef.current = null;
    }

    setState('disconnected');
  }, []);

  // Auto-reconnect when VAD mode changes during active session
  const prevVadModeRef = useRef(vadMode);
  const chatModeRef = useRef<ChatMode>('voice');
  useEffect(() => { chatModeRef.current = chatMode; }, [chatMode]);

  useEffect(() => {
    if (prevVadModeRef.current === vadMode) return;
    prevVadModeRef.current = vadMode;

    // Only reconnect if we're currently connected
    if (state === 'connected' || state === 'speaking' || state === 'listening') {
      const mode = chatModeRef.current;
      disconnect();
      // Small delay to let disconnect complete before reconnecting
      setTimeout(() => connect(mode), 300);
    }
  }, [vadMode, state, disconnect, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    // State
    state,
    chatMode,
    error,
    muted,
    pushToTalkMode,
    audioDetectedWhileMuted,

    // Actions
    connect,
    disconnect,
    closeChat,
    sendContext,
    sendTextMessage,
    toggleMute,
    setPushToTalkMode,
    startTalking,
    stopTalking,
  };
}
